import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { Link } from "tsi-common-react/src/components/Link/index";
import { ProductUUID } from "tsi-common-react/src/models/nominals";
import { focusElement } from "tsi-common-react/src/utils/keyboardFocus";
import { getPageSetting } from "tsi-common-react/src/utils/settings";
import { usePageURL, useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import iconXClose from "../../../assets/svg/x-close.svg";
import {
    buildRetailModalFinancingTrigger,
    // TODO in 2019 Q2 this guide: Don't import things across applications
    buildRetailModalPreapprovalTrigger,
} from "../../checkout/index";
import { NearbyFlagshipStores } from "../../retail-locator/NearbyFlagshipStores";
import { NearbyStoresProduct } from "../../retail-locator/NearbyStoresProduct";
import { PDPModalLocationSearch } from "../../retail-locator/PDPModalLocationSearch";

import "./index.scss";

const MODAL_STYLE = {
    content: {
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        margin: "auto",
        boxShadow: "none",
    },
};

const getModalContentClass = (emptyStage: boolean): string => {
    return classNames({
        "pdp-modal__content": true,
        "pdp-modal__content--empty-state": !!emptyStage,
    });
};

const handleModalAfterOpen = () => {
    focusElement(".pdp-modal__close button--ghost");
};

export const RetailModal = (props: {
    productTitle: string;
    productUUID: ProductUUID;
    defaultOpen?: boolean;
    showPrequal: boolean;
}): React.ReactNode => {
    const staticURL = useStaticURL();
    const [modalIsOpen, setModalIsOpen] = React.useState(!!props.defaultOpen);
    const [emptyStage, setEmptyStage] = React.useState(false);
    const mattressesLink = usePageURL("mattresses_link");

    const onOpenModal = (event?: React.MouseEvent<HTMLElement>) => {
        if (event) {
            event.preventDefault();
        }
        setModalIsOpen(true);
    };

    const onCloseModal = (
        event?: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => {
        if (event) {
            event.preventDefault();
        }
        setModalIsOpen(false);
    };

    const displayEmptyState = (state: boolean) => {
        if (emptyStage !== state) {
            setEmptyStage(state);
        }
    };

    const modalContentClass = getModalContentClass(emptyStage);

    return (
        <>
            <button
                className="pdp-modal__trigger-link al-pdp-modal__trigger-link button--ghost"
                onClick={onOpenModal}
            >
                {t`More Ways to Buy`}
            </button>
            <Modal
                aria={{ modal: true }}
                contentLabel={t`Visit a Retail Store`}
                className="pdp-modal"
                isOpen={modalIsOpen}
                onAfterOpen={handleModalAfterOpen}
                onRequestClose={onCloseModal}
                role="dialog"
                style={MODAL_STYLE}
            >
                <button
                    aria-label={t`Close Modal`}
                    className="pdp-modal__close button--ghost"
                    onClick={onCloseModal}
                >
                    <SVG
                        aria-hidden="true"
                        className="pdp-modal__close--icon"
                        src={iconXClose}
                        title={t`Close Icon`}
                    />
                </button>
                <div className="pdp-modal__container">
                    <div className="pdp-modal__buy-direct">
                        <div className="pdp-modal__header pdp-modal__header--narrow">
                            <div className="pdp-modal__header-wrapper u-flex-container">
                                <h2 className="pdp-modal__title">
                                    {t`Buy Direct From Us`}
                                </h2>
                            </div>
                        </div>
                        <div className="pdp-modal-cta">
                            <div className="pdp-modal-cta__item pdp-modal__block">
                                <h5 className="pdp-modal__subtitle">
                                    {t`Buy Now Online`}
                                </h5>
                                <Link
                                    href={mattressesLink}
                                    className="pdp-modal__link pdp-modal-cta__link al-more-ways-to-buy-close_modal"
                                >
                                    {t`View Mattresses`}
                                </Link>
                            </div>
                            <div className="pdp-modal-cta__item pdp-modal__block">
                                <h5 className="pdp-modal__subtitle">
                                    {t`Call a Sleep Expert`}
                                </h5>
                                <a
                                    href={
                                        getPageSetting(
                                            "pre_order_phone_number_link",
                                        ) || ""
                                    }
                                    className="pdp-modal__link pdp-modal-cta__link al-more-ways-to-buy-phone"
                                >
                                    {getPageSetting(
                                        "pre_order_phone_number_display",
                                    )}
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="pdp-modal__locator-content">
                        <div className="pdp-modal__header pdp-modal__header--search">
                            <div className="pdp-modal__header-wrapper u-flex-container">
                                <h1 className="pdp-modal__title">
                                    <div className="pdp-modal__title-wrapper u-flex-container">
                                        {t`Visit your Tempur-Pedic before you buy`}
                                    </div>
                                </h1>
                                <div className="pdp-modal__search">
                                    <PDPModalLocationSearch classNames="pdp-modal__input pdp-modal-locator__input al-pdp-modal__locator-input al-more-ways-to-buy-zip" />
                                </div>
                            </div>
                        </div>
                        {!emptyStage && (
                            <div className="pdp-modal-available">
                                {t`Your ${props.productTitle} is on display at the following locations.`}
                            </div>
                        )}
                        <div className="pdp-modal__content-container">
                            <div className={modalContentClass}>
                                <NearbyFlagshipStores
                                    productTitle={props.productTitle}
                                    displayEmptyState={(state) => {
                                        displayEmptyState(state);
                                    }}
                                />
                            </div>
                            <div className="pdp-modal-locator">
                                {!emptyStage ? (
                                    <>
                                        <h5 className="pdp-modal__subtitle pdp-modal__subtitle--alt">
                                            {t`Shop Local Retailers`}
                                        </h5>
                                        <div className="pdp-modal__header pdp-modal__header--narrow">
                                            <div className="pdp-modal__title">
                                                {t`Local Retailers`}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <img
                                        className="pdp-modal__empty-state"
                                        aria-hidden="true"
                                        alt=""
                                        src={`${staticURL}img/pdp/pdp-modal-empty-state.png`}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                )}
                                <NearbyStoresProduct
                                    product={props.productUUID}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pdp-modal-adspace">
                    {props.showPrequal
                        ? buildRetailModalPreapprovalTrigger(onCloseModal)
                        : buildRetailModalFinancingTrigger()}
                </div>
            </Modal>
        </>
    );
};
